import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'

import { validateField, validateOptionalField, DESCRIPTION_MAX_LENGTH, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import CoreResourceFields from '../shared/CoreResourceFields'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { RequestFormStyle } from '../styles'
import { setErrorNotification, setSuccessNotification } from '../../reducers/notificationReducer'
import { createBook } from '../../reducers/booksReducer'
import useResourceSubmit from '../../hooks/useResourceSubmit'

import resourceService from '../../services/resources'

const BookForm = () => {
    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [name, setName] = useState('')
    const [nameError, setNameError] = useState(false)
    const [nameHelper, setNameHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [bookType, setBookType] = useState('TB')
    const [authors, setAuthors] = useState('')
    const [authorsError, setAuthorsError] = useState(false)
    const [authorsHelper, setAuthorsHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [date, setDate] = useState('')
    const [dateError, setDateError] = useState(false)
    const [dateHelper, setDateHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)

    const [isbn, setIsbn] = useState('')
    const [isbnError, setIsbnError] = useState(false)
    const [isbnHelper, setIsbnHelper] = useState('')
    const [isbnBook, setIsbnBook] = useState('')
    const [isbnBookError, setIsbnBookError] = useState(false)
    const [isbnBookHelper, setIsbnBookHelper] = useState('')
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])

    const reset = () => {
        setName('')
        setNameError(false)
        setNameHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setAuthors('')
        setAuthorsError(false)
        setAuthorsHelper('')
        setLink('')
        setLinkError(false)
        setLinkHelper('')
        setDate('')
        setDateError(false)
        setDateHelper('')
        setContentTags([])
        setMedicalTags([])
        setAdminCategoryTags([])
        setExperienceTags([])
        setDemographicTags([])
        setShowTagErrors(false)
        setBookType('TB')
        setIsbnBook('')
        setIsSeminalWork(false)
        setInQuiz(false)
        setIsTcairem(false)
        setIsPaid(false)
        setTcairemTags([])
        setPersonaTags([])
    }


    const handleSubmit = (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (
                validateField(name, setNameError, setNameHelper, 200, 'Title') &&
                validateOptionalField(description, setDescriptionError, setDescriptionHelper, DESCRIPTION_MAX_LENGTH, 'Description') &&
                validateField(authors, setAuthorsError, setAuthorsHelper, 100, 'Authors') &&
                validateField(link, setLinkError, setLinkHelper) &&
                validateField(date, setDateError, setDateHelper) &&
                validateField(isbnBook, setIsbnBookError, setIsbnBookHelper, 50, 'ISBN') &&
                validateRequiredResourceTags({
                    contentTags,
                    adminCategoryTags,
                    experienceTags,
                    demographicTags,
                    tcairemTags,
                    personaTags,
                    isTcairem,
                })
            ) {

            const data = {
                title: name,
                description: description,
                authors: authors,
                link: link,
                pub_date: date,
                isbn: isbnBook,
                book_type: bookType,
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
            }

            submit(createBook(data), reset)
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }

    const handleSearch = async () => {
        if (isbn === '') {
            setIsbnError(true)
            setIsbnHelper('Please fill this field first!')
            return
        }
        setIsbnError(false)
        setIsbnHelper('')

        try {
            const res = await resourceService.retrieveBook(isbn)
            setName(res.volumeInfo.title)
            setDescription(res.volumeInfo.description)
            setAuthors(res.volumeInfo.authors.join(', '))
            setDate(res.volumeInfo.publishedDate)
            setLink(res.volumeInfo.infoLink)
            setIsbnBook(isbn)
            dispatch(setSuccessNotification('Book Found'))
        } catch {
            dispatch(setErrorNotification('A book could not be found with that ISBN'))
        }

        setIsbn('')
    }

    return (
        <>

            <Box sx={RequestFormStyle.searchContainer}>
                <Box sx={RequestFormStyle.searchTextContainer} >
                    <Typography {...RequestFormStyle.searchText} gutterBottom>
                        Need help filling fields? You can fill fields automatically by searching with the book ISBN below.
                        Note you will still need to fill the key words.
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            value={isbn}
                            onChange={e => setIsbn(e.target.value)}
                            label="ISBN"
                            helperText={isbnHelper}
                            error={isbnError}
                        />
                    </Grid>
                    <Grid item xs={4} md={2}>
                        <Button {...RequestFormStyle.searchButton.props} sx={RequestFormStyle.searchButton.style} fullWidth onClick={()=>handleSearch()}>
                            Search ISBN
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <br />
            <Container {...RequestFormStyle.container}>
                <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                    Book Form
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl>
                                <FormLabel id="book-type">Book Type</FormLabel>
                                    <RadioGroup row aria-labelledby="book-type" name="book-types" defaultValue={"TB"}>
                                        <FormControlLabel value="TB" control={<Radio onClick={(e) => setBookType(e.target.value)} />} label="Textbook" />
                                        <FormControlLabel value="OT" control={<Radio onClick={(e) => setBookType(e.target.value)} />} label="Other" />
                                    </RadioGroup>
                            </FormControl>
                        </Grid>
                        <CoreResourceFields
                            name={name} setName={setName} nameError={nameError} setNameError={setNameError} nameHelper={nameHelper} setNameHelper={setNameHelper}
                            authors={authors} setAuthors={setAuthors} authorsError={authorsError} setAuthorsError={setAuthorsError} authorsHelper={authorsHelper} setAuthorsHelper={setAuthorsHelper}
                            link={link} setLink={setLink} linkError={linkError} setLinkError={setLinkError} linkHelper={linkHelper} setLinkHelper={setLinkHelper}
                            date={date} setDate={setDate} dateError={dateError} setDateError={setDateError} dateHelper={dateHelper} setDateHelper={setDateHelper}
                            validate={validateField}
                            authorsLabel="Authors"
                            showCharCount={true}
                            nameMaxLength={200}
                            authorsMaxLength={100}
                        />
                        <ResourceDescriptionField
                            value={description} setValue={setDescription}
                            error={descriptionError} setError={setDescriptionError}
                            helper={descriptionHelper} setHelper={setDescriptionHelper}
                        />
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                value={isbnBook}
                                onChange={e => setIsbnBook(e.target.value)}
                                label="Book ISBN"
                                required
                                error={isbnBookError}
                                helperText={isbnBookHelper || `${isbnBook.length}/50 characters`}
                                onBlur={()=>validateField(isbnBook, setIsbnBookError, setIsbnBookHelper, 50, 'ISBN')}
                            />
                        </Grid>
                        <ResourceTagsSection
                            contentTags={contentTags} setContentTags={setContentTags}
                            medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                            adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                            experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                            required={true}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                        <TCAIREMFieldsSection
                            isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                            isPaid={isPaid} setIsPaid={setIsPaid}
                            tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                            personaTags={personaTags} setPersonaTags={setPersonaTags}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4}>
                            <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>Create Book</Button>
                        </Grid>
                    </Grid>
                </form>
            </Container>
        </>
    )
}

export default BookForm
